import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, Copy, Plus, RefreshCw, Save, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import StoreLayout from "./StoreLayout";
import { preferredStoreLocationId, rememberedStoreLocationId, rememberStoreLocationId } from "./storeLocationPreference";
import {
  StoreApiError,
  StoreBookingAvailability,
  StoreBookingLocation,
  StoreUser,
  bulkGenerateStoreBookingAvailability,
  getCurrentStoreUser,
  listStoreBookingAvailability,
  logoutStore,
  saveStoreBookingAvailability,
} from "./api";

const defaultSlots = ["09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00"];
const weekdayOptions = [
  { value: 1, label: "一" },
  { value: 2, label: "二" },
  { value: 3, label: "三" },
  { value: 4, label: "四" },
  { value: 5, label: "五" },
  { value: 6, label: "六" },
  { value: 0, label: "日" },
];

const inputClass = "h-11 min-w-0 w-full rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";

type CoverageStatus = {
  ConfiguredThrough: string | null;
  DaysRemaining: number | null;
  WarningThresholdDays: number;
};

function formatDate(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function monthStart(value: string) {
  const [year, month] = value.split("-").map(Number);
  return new Date(year, month - 1, 1);
}

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(date: Date, months: number) {
  return new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
}

function monthEnd(date: Date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function daysForMonth(monthValue: string) {
  const start = monthStart(monthValue);
  const firstGridDay = addDays(start, -start.getDay());
  return Array.from({ length: 42 }, (_, index) => addDays(firstGridDay, index));
}

function dateLabel(value: string) {
  const [year, month, day] = value.split("-");
  return `${year}/${month}/${day}`;
}

function sortSlots(values: string[]) {
  return Array.from(new Set(values.filter((value) => /^\d{2}:\d{2}$/.test(value)))).sort();
}

function minutesFromTime(value: string) {
  const [hour, minute] = value.split(":").map(Number);
  return hour * 60 + minute;
}

function timeFromMinutes(value: number) {
  const hour = Math.floor(value / 60);
  const minute = value % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function generateSlots(start: string, end: string, interval: number) {
  const startMinutes = minutesFromTime(start);
  const endMinutes = minutesFromTime(end);
  if (endMinutes < startMinutes || interval < 5) return [];
  const slots: string[] = [];
  for (let current = startMinutes; current <= endMinutes; current += interval) {
    slots.push(timeFromMinutes(current));
  }
  return slots;
}

export default function StoreSchedule() {
  const navigate = useNavigate();
  const bulkFormRef = useRef<HTMLFormElement | null>(null);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [user, setUser] = useState<StoreUser | null>(null);
  const [locations, setLocations] = useState<StoreBookingLocation[]>([]);
  const [locationId, setLocationId] = useState(() => rememberedStoreLocationId() || "");
  const [month, setMonth] = useState(currentMonth);
  const [items, setItems] = useState<StoreBookingAvailability[]>([]);
  const [selectedDate, setSelectedDate] = useState(formatDate(today));
  const [isOpen, setIsOpen] = useState(true);
  const [isNationalHoliday, setIsNationalHoliday] = useState(false);
  const [slots, setSlots] = useState<string[]>(defaultSlots);
  const [newSlot, setNewSlot] = useState("09:00");
  const [generateStart, setGenerateStart] = useState("09:00");
  const [generateEnd, setGenerateEnd] = useState("17:00");
  const [generateInterval, setGenerateInterval] = useState("30");
  const [excludeBreak, setExcludeBreak] = useState(true);
  const [breakStart, setBreakStart] = useState("12:00");
  const [breakEnd, setBreakEnd] = useState("13:30");
  const [slotCapacity, setSlotCapacity] = useState("1");
  const [note, setNote] = useState("");
  const [bulkStart, setBulkStart] = useState(formatDate(today));
  const [bulkEnd, setBulkEnd] = useState(formatDate(addDays(today, 60)));
  const [bulkWeekdays, setBulkWeekdays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [bulkOverwrite, setBulkOverwrite] = useState(false);
  const [coverage, setCoverage] = useState<CoverageStatus | null>(null);
  const [bookingPublicLookaheadDays, setBookingPublicLookaheadDays] = useState(180);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [message, setMessage] = useState("");

  const startDate = `${month}-01`;
  const endDate = formatDate(monthEnd(monthStart(month)));
  const itemMap = useMemo(() => new Map(items.map((item) => [item.AppointmentDate, item])), [items]);
  const selectedItem = itemMap.get(selectedDate);
  const selectedBooked = selectedItem?.Slots.reduce((sum, slot) => sum + slot.Booked, 0) || 0;
  const activeLocation = locations.find((location) => location.LocationId === locationId) || locations[0] || null;
  const coverageNeedsAttention = coverage?.DaysRemaining === null || coverage?.DaysRemaining === undefined || coverage.DaysRemaining < coverage.WarningThresholdDays;
  const coverageText = coverage?.ConfiguredThrough
    ? `目前已設定到 ${dateLabel(coverage.ConfiguredThrough)}，約剩 ${coverage.DaysRemaining ?? 0} 天。`
    : "目前尚未建立可預約日期，官網不會顯示可選日期。";

  async function load(nextLocationId = locationId) {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentStoreUser();
      setUser(me.User);
      const preferredLocationId = preferredStoreLocationId(me.User, nextLocationId);
      const data = await listStoreBookingAvailability({ LocationId: preferredLocationId, StartDate: startDate, EndDate: endDate });
      setLocations(data.Locations);
      const resolvedLocationId = preferredLocationId || data.SelectedLocationId || data.Locations[0]?.LocationId || "";
      setLocationId(resolvedLocationId);
      rememberStoreLocationId(resolvedLocationId);
      if (resolvedLocationId !== preferredLocationId) {
        const nextData = await listStoreBookingAvailability({ LocationId: resolvedLocationId, StartDate: startDate, EndDate: endDate });
        setItems(nextData.Items);
        setCoverage(nextData.Coverage ?? null);
        setBookingPublicLookaheadDays(nextData.BookingPublicLookaheadDays || 180);
      } else {
        setItems(data.Items);
        setCoverage(data.Coverage ?? null);
        setBookingPublicLookaheadDays(data.BookingPublicLookaheadDays || 180);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "預約時段資料載入失敗。");
      if (error instanceof StoreApiError && error.status === 401) {
        navigate("/store-login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  useEffect(() => {
    const item = itemMap.get(selectedDate);
    setIsOpen(item?.IsOpen ?? true);
    setIsNationalHoliday(item?.IsNationalHoliday ?? false);
    setSlots(item?.RawSlots && item.RawSlots.length > 0 ? sortSlots(item.RawSlots) : defaultSlots);
    setSlotCapacity(String(item?.SlotCapacity || 1));
    setNote(item?.Note || "");
  }, [selectedDate, itemMap]);

  async function handleLogout() {
    await logoutStore().catch(() => undefined);
    navigate("/store-login", { replace: true });
  }

  function addSlot(value: string) {
    if (!/^\d{2}:\d{2}$/.test(value)) {
      setMessage("請輸入正確的時段格式，例如 09:00。");
      return;
    }
    setMessage("");
    setSlots((current) => sortSlots([...current, value]));
  }

  function removeSlot(value: string) {
    setSlots((current) => current.filter((slot) => slot !== value));
  }

  function applyGeneratedSlots(mode: "replace" | "append") {
    const interval = Number(generateInterval) || 30;
    let generated = generateSlots(generateStart, generateEnd, interval);
    if (excludeBreak) {
      const breakStartMinutes = minutesFromTime(breakStart);
      const breakEndMinutes = minutesFromTime(breakEnd);
      generated = generated.filter((slot) => {
        const minutes = minutesFromTime(slot);
        return minutes < breakStartMinutes || minutes >= breakEndMinutes;
      });
    }
    setSlots((current) => sortSlots(mode === "replace" ? generated : [...current, ...generated]));
  }

  function applyPreset(type: "weekday" | "morning" | "afternoon" | "clear") {
    if (type === "weekday") setSlots(defaultSlots);
    if (type === "morning") setSlots(defaultSlots.filter((slot) => minutesFromTime(slot) < 12 * 60));
    if (type === "afternoon") setSlots(defaultSlots.filter((slot) => minutesFromTime(slot) >= 12 * 60));
    if (type === "clear") setSlots([]);
  }

  function calendarMonthValue(date: Date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  }

  function selectedDateForMonth(monthValue: string) {
    return monthValue === currentMonth ? formatDate(today) : formatDate(monthStart(monthValue));
  }

  function handleMonthChange(monthValue: string) {
    setMonth(monthValue);
    setSelectedDate(selectedDateForMonth(monthValue));
  }

  function applyBulkRange(type: "currentMonth" | "nextMonth" | "extendLookahead") {
    let nextStart = "";
    let nextEnd = "";
    let label = "";
    if (type === "currentMonth") {
      nextStart = formatDate(today);
      nextEnd = formatDate(monthEnd(today));
      label = "本月剩餘";
    } else if (type === "nextMonth") {
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      nextStart = formatDate(nextMonth);
      nextEnd = formatDate(monthEnd(nextMonth));
      label = "下個月";
    } else {
      nextStart = formatDate(today);
      nextEnd = formatDate(addDays(today, bookingPublicLookaheadDays - 1));
      label = `官網開放天數 ${bookingPublicLookaheadDays} 天`;
    }
    setBulkStart(nextStart);
    setBulkEnd(nextEnd);
    setMessage(`已套用「${label}」：${dateLabel(nextStart)} 到 ${dateLabel(nextEnd)}。確認星期、時段與覆蓋設定後，再按「批次產生日期」。`);
    window.setTimeout(() => bulkFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  }

  function changeCalendarMonth(delta: number) {
    handleMonthChange(calendarMonthValue(addMonths(monthStart(month), delta)));
  }

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!locationId || !selectedDate) {
      setMessage("請先選擇門市與日期。");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      await saveStoreBookingAvailability({
        LocationId: locationId,
        AppointmentDate: selectedDate,
        IsOpen: isOpen,
        Slots: slots,
        SlotCapacity: Number(slotCapacity) || 1,
        IsNationalHoliday: isNationalHoliday,
        Note: note.trim() || undefined,
      });
      await load(locationId);
      setMessage("預約時段設定已儲存。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "預約時段設定儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleBulkGenerate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!locationId) {
      setMessage("請先選擇門市。");
      return;
    }
    if (bulkWeekdays.length === 0) {
      setMessage("請至少選擇一個星期。");
      return;
    }
    setBulkSaving(true);
    setMessage("");
    try {
      const result = await bulkGenerateStoreBookingAvailability({
        LocationId: locationId,
        StartDate: bulkStart,
        EndDate: bulkEnd,
        Weekdays: bulkWeekdays,
        Slots: slots,
        SlotCapacity: Number(slotCapacity) || 1,
        Overwrite: bulkOverwrite,
      });
      await load(locationId);
      setMessage(`批次產生完成：新增 ${result.Created} 天、更新 ${result.Updated} 天、略過 ${result.Skipped} 天。`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "批次產生失敗。");
    } finally {
      setBulkSaving(false);
    }
  }

  return (
    <StoreLayout currentLocationId={locationId || preferredStoreLocationId(user, locationId)} onLocationChange={(nextLocationId) => void load(nextLocationId)} title="門市預約時段" user={user} onLogout={handleLogout}>
      {message && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{message}</div>}

      <section className={`mb-5 rounded-md border p-4 ${coverageNeedsAttention ? "border-[#f0c678] bg-[#fff9ed]" : "border-[#dfe6e1] bg-white"}`}>
        <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:justify-between">
          <div className="flex min-w-0 gap-3">
            <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${coverageNeedsAttention ? "bg-[#fff1dc] text-[#8a5b14]" : "bg-[#e9f4ec] text-[#245438]"}`}>
              <AlertTriangle size={18} />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-[#203d32]">{activeLocation?.Name || "目前門市"}預約日期覆蓋</h2>
              <p className="mt-1 text-sm text-[#59665f]">{coverageText}</p>
              <p className="mt-1 text-xs text-[#9a8a7a]">這裡只會調整目前門市的官網可預約日期、時段與容量；沒有設定的日期會視為不可預約。</p>
            </div>
          </div>
          <div className="rwd-action-grid sm:grid-cols-3 2xl:w-auto">
            <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={() => applyBulkRange("currentMonth")}>本月剩餘</button>
            <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={() => applyBulkRange("nextMonth")}>下個月</button>
            <button className="h-10 rounded-md bg-[#315746] px-3 text-sm font-bold text-white hover:bg-[#203d32]" type="button" onClick={() => applyBulkRange("extendLookahead")}>官網 {bookingPublicLookaheadDays} 天</button>
          </div>
        </div>
      </section>

      <div className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-4 sm:grid-cols-2 2xl:grid-cols-[minmax(220px,320px)_180px_auto] 2xl:items-end">
        <label className="grid gap-1 text-sm font-bold text-[#315746]">
          <span>門市</span>
          <select className={inputClass} value={locationId} onChange={(event) => void load(event.target.value)}>
            {locations.map((location) => <option key={location.LocationId} value={location.LocationId}>{location.Name}</option>)}
          </select>
        </label>
        <label className="grid gap-1 text-sm font-bold text-[#315746]">
          <span>月份</span>
          <input className={inputClass} type="month" value={month} onChange={(event) => handleMonthChange(event.target.value)} />
        </label>
        <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => load(locationId)} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
      </div>

      <div className="grid gap-5 2xl:grid-cols-[minmax(0,1fr)_480px]">
        <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-bold text-[#203d32]">月曆總覽</h2>
              <p className="text-xs text-[#9a8a7a]">點選日期後，在設定區調整開放狀態與時段。</p>
            </div>
            <div className="grid grid-cols-[44px_minmax(76px,1fr)_44px] gap-2 sm:flex sm:items-center">
              <button className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={() => changeCalendarMonth(-1)} title="上一月">
                <ChevronLeft size={18} />
              </button>
              <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={() => handleMonthChange(currentMonth)}>
                <CalendarDays size={16} />
                本月
              </button>
              <button className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={() => changeCalendarMonth(1)} title="下一月">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          <div className="overflow-hidden rounded-md border border-[#dfe6e1] bg-[#f6f8f6]">
            <div className="grid grid-cols-7 border-b border-[#dfe6e1] bg-[#eef5f0] text-center text-xs font-bold text-[#315746]">
              {["日", "一", "二", "三", "四", "五", "六"].map((label, index) => (
                <div className={`py-2.5 ${index === 0 || index === 6 ? "bg-[#fff8ea] text-[#8a5b14]" : ""}`} key={label}>{label}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-px bg-[#dfe6e1]">
              {daysForMonth(month).map((date) => {
                const value = formatDate(date);
                const item = itemMap.get(value);
                const inMonth = value.startsWith(month);
                const selected = selectedDate === value;
                const totalSlots = item?.Slots.length || 0;
                const availableSlots = item?.Slots.filter((slot) => slot.Available).length || 0;
                const statusLabel = !item ? "未設定" : item.IsOpen ? `${availableSlots}/${totalSlots}` : "休息";
                const statusClass = !item ? "bg-[#f7f6f2] text-[#9a8a7a]" : item.IsOpen ? availableSlots > 0 ? "bg-[#e6f3eb] text-[#245438]" : "bg-[#fff1dc] text-[#8a5b14]" : "bg-[#f2e9e8] text-[#9a2d2d]";
                const sideBarClass = !item ? "bg-[#d8d2c8]" : item.IsOpen ? availableSlots > 0 ? "bg-[#315746]" : "bg-[#c48a2c]" : "bg-[#bf6b63]";
                return (
                  <button
                    className={`group relative min-h-[82px] bg-white p-2 text-left transition hover:z-10 hover:bg-[#fbfcfb] hover:shadow-[0_0_0_1px_#b8c8bf] focus:outline-none focus:ring-2 focus:ring-[#315746]/25 sm:min-h-[96px] ${selected ? "z-10 bg-[#f2faf5] shadow-[inset_0_0_0_2px_#315746]" : ""} ${inMonth ? "" : "bg-[#fafafa] text-[#b5bdb7]"}`}
                    key={value}
                    onClick={() => setSelectedDate(value)}
                    type="button"
                  >
                    <span className={`absolute inset-y-2 left-0 w-1 rounded-r-full ${sideBarClass} ${inMonth ? "" : "opacity-40"}`} />
                    <div className="flex items-center justify-between gap-1">
                      <span className={`grid h-7 w-7 place-items-center rounded-full text-sm font-bold ${selected ? "bg-[#315746] text-white" : inMonth ? "text-[#203d32]" : "text-[#b5bdb7]"}`}>{date.getDate()}</span>
                      {item?.IsNationalHoliday && <span className="hidden rounded-full bg-[#fff1dc] px-1.5 py-0.5 text-[10px] font-bold text-[#8a5b14] sm:inline">特殊</span>}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-1.5 pl-1">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${statusClass}`}>{statusLabel}</span>
                      {item?.IsOpen && <span className="hidden text-[11px] text-[#7b877f] sm:inline">時段</span>}
                    </div>
                    {item?.Note && <div className="mt-2 hidden truncate pl-1 text-[11px] text-[#9a8a7a] sm:block">{item.Note}</div>}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs font-medium text-[#7b877f]">
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#315746]" />可預約</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#bf6b63]" />休息</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#d8d2c8]" />未設定</span>
          </div>
        </section>

        <aside className="min-w-0 space-y-5">
          <form className="rounded-md border border-[#dfe6e1] bg-white p-4" onSubmit={handleSave}>
            <h2 className="mb-1 text-lg font-bold text-[#203d32]">{dateLabel(selectedDate)} 設定</h2>
            <p className="mb-4 text-xs text-[#9a8a7a]">已預約數：{selectedBooked}。關閉已有預約的日期不會自動取消舊預約。</p>

            <div className="space-y-4">
              <label className="flex items-center justify-between gap-4 rounded-md border border-[#dfe6e1] p-3">
                <span>
                  <span className="block text-sm font-bold text-[#315746]">開放預約</span>
                  <span className="block text-xs text-[#7b877f]">關閉後官網不可選此日期。</span>
                </span>
                <input checked={isOpen} className="h-5 w-5 accent-[#315746]" type="checkbox" onChange={(event) => setIsOpen(event.target.checked)} />
              </label>

              <label className="flex items-center justify-between gap-4 rounded-md border border-[#dfe6e1] p-3">
                <span>
                  <span className="block text-sm font-bold text-[#315746]">店休 / 特殊日</span>
                  <span className="block text-xs text-[#7b877f]">勾選時會自動關閉預約。</span>
                </span>
                <input
                  checked={isNationalHoliday}
                  className="h-5 w-5 accent-[#315746]"
                  type="checkbox"
                  onChange={(event) => {
                    setIsNationalHoliday(event.target.checked);
                    if (event.target.checked) setIsOpen(false);
                  }}
                />
              </label>

              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>每時段容量</span>
                <input className={inputClass} min={1} max={99} type="number" value={slotCapacity} onChange={(event) => setSlotCapacity(event.target.value)} />
              </label>

              <section className="rounded-md border border-[#dfe6e1] p-3">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-bold text-[#315746]">時段編輯</h3>
                    <p className="text-xs text-[#9a8a7a]">先快速產生，再用下方標籤微調。</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-[#e9f4ec] px-2.5 py-1 text-xs font-bold text-[#245438]">{slots.length} 個</span>
                </div>

                <div className="grid gap-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="grid gap-1 text-xs font-bold text-[#59665f]">
                      <span>開始</span>
                      <input className={inputClass} type="time" value={generateStart} onChange={(event) => setGenerateStart(event.target.value)} />
                    </label>
                    <label className="grid gap-1 text-xs font-bold text-[#59665f]">
                      <span>結束</span>
                      <input className={inputClass} type="time" value={generateEnd} onChange={(event) => setGenerateEnd(event.target.value)} />
                    </label>
                    <label className="grid gap-1 text-xs font-bold text-[#59665f]">
                      <span>間隔</span>
                      <select className={inputClass} value={generateInterval} onChange={(event) => setGenerateInterval(event.target.value)}>
                        <option value="15">15 分</option>
                        <option value="30">30 分</option>
                        <option value="60">60 分</option>
                      </select>
                    </label>
                    <button className="inline-flex h-11 items-center justify-center self-end rounded-md bg-[#315746] px-3 text-sm font-bold text-white hover:bg-[#203d32]" type="button" onClick={() => applyGeneratedSlots("replace")}>
                      產生
                    </button>
                  </div>

                  <div className="rounded-md border border-[#edf0ee] bg-white p-3">
                    <label className="mb-3 flex items-center gap-2 text-sm font-bold text-[#315746]">
                      <input checked={excludeBreak} className="h-4 w-4 accent-[#315746]" type="checkbox" onChange={(event) => setExcludeBreak(event.target.checked)} />
                      排除午休
                    </label>
                    <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center">
                      <input className="h-11 min-w-0 rounded-md border border-[#cfd8d2] px-3 text-sm outline-none disabled:bg-[#f6f7f5]" disabled={!excludeBreak} type="time" value={breakStart} onChange={(event) => setBreakStart(event.target.value)} />
                      <span className="text-center text-sm font-bold text-[#59665f]">到</span>
                      <input className="h-11 min-w-0 rounded-md border border-[#cfd8d2] px-3 text-sm outline-none disabled:bg-[#f6f7f5]" disabled={!excludeBreak} type="time" value={breakEnd} onChange={(event) => setBreakEnd(event.target.value)} />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button className="rounded-md border border-[#cfd8d2] bg-white px-3 py-2 text-xs font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={() => applyPreset("weekday")}>平日全天</button>
                    <button className="rounded-md border border-[#cfd8d2] bg-white px-3 py-2 text-xs font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={() => applyPreset("morning")}>上午</button>
                    <button className="rounded-md border border-[#cfd8d2] bg-white px-3 py-2 text-xs font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={() => applyPreset("afternoon")}>下午</button>
                    <button className="rounded-md border border-[#cfd8d2] bg-white px-3 py-2 text-xs font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={() => applyGeneratedSlots("append")}>加入產生結果</button>
                    <button className="rounded-md border border-[#e3c8c6] bg-white px-3 py-2 text-xs font-bold text-[#9a2d2d] hover:bg-[#fdf3f2]" type="button" onClick={() => applyPreset("clear")}>清空</button>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_104px]">
                    <input className={inputClass} type="time" value={newSlot} onChange={(event) => setNewSlot(event.target.value)} />
                    <button className="inline-flex h-11 shrink-0 items-center justify-center gap-1 rounded-md border border-[#315746] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={() => addSlot(newSlot)}>
                      <Plus size={16} />
                      新增
                    </button>
                  </div>

                  <div className="min-h-[50px] rounded-md border border-[#edf0ee] bg-[#f8faf8] p-2">
                    {slots.length === 0 ? (
                      <p className="px-2 py-2 text-sm text-[#9a8a7a]">尚未設定時段，官網不會顯示可預約時段。</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {slots.map((slot) => (
                          <span className="inline-flex h-10 min-w-[92px] items-center justify-between gap-1 rounded-full border border-[#cfd8d2] bg-white pl-4 pr-1 text-sm font-bold text-[#315746]" key={slot}>
                            {slot}
                            <button className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[#7b877f] hover:bg-[#f0f5f2] hover:text-[#9a2d2d]" type="button" onClick={() => removeSlot(slot)} title={`移除 ${slot}`}>
                              <X size={14} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>備註</span>
                <input className={inputClass} placeholder="例如：門市教育訓練、臨時店休、加開下午時段" value={note} onChange={(event) => setNote(event.target.value)} />
              </label>

              <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32] disabled:opacity-60" disabled={saving || loading} type="submit">
                <Save size={18} />
                {saving ? "儲存中..." : "儲存此日期"}
              </button>
            </div>
          </form>

          <form className="scroll-mt-6 rounded-md border border-[#dfe6e1] bg-white p-4" ref={bulkFormRef} onSubmit={handleBulkGenerate}>
            <h2 className="mb-1 text-lg font-bold text-[#203d32]">批次產生</h2>
            <p className="mb-4 text-xs text-[#9a8a7a]">用目前設定區的時段與容量，批次建立目前門市的可預約日期。</p>
            <div className="grid gap-3">
              <div className="grid gap-2 sm:grid-cols-3">
                <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={() => applyBulkRange("currentMonth")}>本月剩餘</button>
                <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={() => applyBulkRange("nextMonth")}>下個月</button>
                <button className="h-10 rounded-md border border-[#315746] bg-[#315746] px-3 text-sm font-bold text-white hover:bg-[#203d32]" type="button" onClick={() => applyBulkRange("extendLookahead")}>官網 {bookingPublicLookaheadDays} 天</button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-1 text-sm font-bold text-[#315746]">
                  <span>開始</span>
                  <input className={inputClass} type="date" value={bulkStart} onChange={(event) => setBulkStart(event.target.value)} />
                </label>
                <label className="grid gap-1 text-sm font-bold text-[#315746]">
                  <span>結束</span>
                  <input className={inputClass} type="date" value={bulkEnd} onChange={(event) => setBulkEnd(event.target.value)} />
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                {weekdayOptions.map((option) => {
                  const checked = bulkWeekdays.includes(option.value);
                  return (
                    <button className={`h-9 w-9 rounded-md border text-sm font-bold ${checked ? "border-[#315746] bg-[#315746] text-white" : "border-[#cfd8d2] bg-white text-[#315746]"}`} key={option.value} onClick={() => setBulkWeekdays((current) => checked ? current.filter((value) => value !== option.value) : [...current, option.value])} type="button">
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <label className="flex items-center gap-2 text-sm font-bold text-[#315746]">
                <input checked={bulkOverwrite} className="h-4 w-4 accent-[#315746]" type="checkbox" onChange={(event) => setBulkOverwrite(event.target.checked)} />
                覆蓋已存在日期
              </label>
              <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border border-[#315746] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2] disabled:opacity-60" disabled={bulkSaving || loading} type="submit">
                <Copy size={18} />
                {bulkSaving ? "產生中..." : "批次產生日期"}
              </button>
            </div>
          </form>
        </aside>
      </div>
    </StoreLayout>
  );
}
